"use client";

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Modal,
  FormGroup,
  Input,
  Select,
  ButtonIcon,
  BoxAlignItems,
  DataTable,
  AlertModal,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Pencil } from "lucide-react";
import useUserServiceCommission from "@/hooks/userServiceCommission/useUserServiceCommission";
import { useEmployee } from "@/hooks";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchUserServiceCommissions } from "@/store/modules/userServiceCommission/thunks";
import { AppDispatch } from "@/store";

interface UserServiceCommissionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId?: number;
  serviceName?: string;
  dict: any;
  onCommissionCreated?: (commission: any) => void;
}

interface CommissionFormData {
  user_id: string;
  commission: string;
}

const UserServiceCommissionModal: React.FC<UserServiceCommissionModalProps> = ({
  open,
  onOpenChange,
  serviceId,
  serviceName,
  dict,
  onCommissionCreated,
}) => {
  const [editingCommissionId, setEditingCommissionId] = useState<number | null>(
    null,
  );
  const [openAlert, setOpenAlert] = useState(false);
  const [commissionToDelete, setCommissionToDelete] = useState<number | null>(
    null,
  );
  const [localCommissions, setLocalCommissions] = useState<any[]>([]); // Para modo de criação
  const dispatch: AppDispatch = useDispatch();

  const {
    userServiceCommissions,
    loading,
    onSubmit: createCommission,
    onUpdate: updateCommission,
    handleDelete,
    setUserServiceCommissionId,
  } = useUserServiceCommission(serviceId);

  const { employees } = useEmployee();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CommissionFormData>();

  useEffect(() => {
    if (open && serviceId) {
      dispatch(
        fetchUserServiceCommissions({
          filter: { service_id: serviceId },
        }),
      );
    }
  }, [open, serviceId, dispatch]);

  useEffect(() => {
    if (!serviceId) {
      if (open) {
        setLocalCommissions([]);
      }
    }
  }, [open, serviceId]);

  const employeeOptions = [
    { value: "", label: "Selecione um funcionário" },
    ...(employees?.data?.map((employee: any) => ({
      value: employee.id.toString(),
      label: employee.name,
    })) || []),
  ];

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "user_name",
      header: "Funcionário",
      cell: ({ row }) => <div>{row.getValue("user_name")}</div>,
    },
    {
      accessorKey: "commission",
      header: "Comissão (%)",
      cell: ({ row }) => <div>{Math.floor(row.getValue("commission"))}%</div>,
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => {
        const commission = row.original;
        const isCreateMode = !serviceId;
        const deleteId = isCreateMode ? row.index : commission.id;

        return (
          <div className="flex justify-end gap-1">
            <ButtonIcon
              icon={<Pencil size={14} />}
              onClick={() => {
                if (isCreateMode) {
                  // No modo de criação, passar o índice
                  const commissionWithIndex = {
                    ...commission,
                    localIndex: row.index,
                  };
                  handleEditCommission(commissionWithIndex);
                } else {
                  handleEditCommission(commission);
                }
              }}
            />
            <ButtonIcon
              icon={<Trash size={14} />}
              hovercolor="red"
              onClick={() => handleDeleteCommission(deleteId)}
            />
          </div>
        );
      },
    },
  ];

  const onSubmitForm = async (data: CommissionFormData) => {
    if (!serviceId && !onCommissionCreated) {
      toast.error("ID do serviço não encontrado");
      return;
    }

    if (!data.user_id) {
      toast.error("Funcionário é obrigatório");
      return;
    }

    if (!data.commission) {
      toast.error("Comissão é obrigatória");
      return;
    }

    try {
      const employee = employees?.data?.find(
        (emp: any) => emp.id.toString() === data.user_id,
      );
      const commissionData = {
        service_id: serviceId?.toString() || "",
        user_id: data.user_id,
        user_name: employee?.name || "Funcionário",
        commission: parseInt(data.commission),
      };

      let success = false;

      if (!serviceId && onCommissionCreated) {
        if (editingCommissionId !== null) {
          setLocalCommissions((prev) =>
            prev.map((comm, index) =>
              index === editingCommissionId ? commissionData : comm,
            ),
          );
        } else {
          const existingIndex = localCommissions.findIndex(
            (comm) => comm.user_id === data.user_id,
          );
          if (existingIndex !== -1) {
            toast.error(
              "Já existe uma comissão configurada para este funcionário",
            );
            return;
          }
          setLocalCommissions((prev) => [...prev, commissionData]);
        }
        onCommissionCreated(commissionData);
        success = true;
      } else if (serviceId) {
        if (editingCommissionId) {
          success = await updateCommission(editingCommissionId, commissionData);
        } else {
          success = await createCommission(commissionData);
        }
      }

      if (success) {
        reset();
        setEditingCommissionId(null);
        if (serviceId) {
          dispatch(
            fetchUserServiceCommissions({
              filter: { service_id: serviceId },
            }),
          );
        }
      }
    } catch (error) {
      console.error("Erro ao salvar comissão:", error);
    }
  };

  const handleEditCommission = (commission: any) => {
    setValue("user_id", commission.user_id.toString());
    setValue("commission", Math.floor(commission.commission).toString());

    if (serviceId) {
      setEditingCommissionId(commission.id);
    } else {
      setEditingCommissionId(commission.localIndex || commission.id);
    }
  };

  const handleDeleteCommission = (commissionId: number) => {
    if (!serviceId) {
      setLocalCommissions((prev) =>
        prev.filter((_, index) => index !== commissionId),
      );
    } else {
      setCommissionToDelete(commissionId);
      setOpenAlert(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!commissionToDelete) return;

    try {
      const success = await handleDelete(commissionToDelete);
      if (success && serviceId) {
        dispatch(
          fetchUserServiceCommissions({
            filter: { service_id: serviceId },
          }),
        );
        setOpenAlert(false);
        setCommissionToDelete(null);
      }
    } catch (error) {
      console.error("Erro ao excluir comissão:", error);
    }
  };

  const handleCancelDelete = () => {
    setOpenAlert(false);
    setCommissionToDelete(null);
  };

  const handleCancel = () => {
    reset();
    setEditingCommissionId(null);
  };

  const handleCloseModal = () => {
    onOpenChange(false);
    reset();
    setEditingCommissionId(null);
  };

  return (
    <Modal
      titleModal={`Comissões Personalizadas - ${serviceName || "Serviço"}`}
      open={open}
      onOpenChange={handleCloseModal}
    >
      <div className="space-y-6">
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
          <FormGroup>
            <Select
              {...register("user_id", {
                required: "Funcionário é obrigatório",
              })}
              label="Funcionário"
              options={employeeOptions}
            />
            {errors.user_id && (
              <p className="text-red-500 text-sm">{errors.user_id.message}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Input
              {...register("commission", {
                required: "Comissão é obrigatória",
              })}
              label="Comissão (%)"
              type="number"
              min="0"
              max="100"
              placeholder="Ex: 15"
            />
            {errors.commission && (
              <p className="text-red-500 text-sm">
                {errors.commission.message}
              </p>
            )}
          </FormGroup>

          <BoxAlignItems gap="10px">
            <Button
              type="submit"
              disabled={isSubmitting || loading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus size={16} />
              {editingCommissionId ? "Atualizar" : "Adicionar"}
            </Button>
            {editingCommissionId && (
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
          </BoxAlignItems>
        </form>

        <div className="border-t pt-4">
          <h3 className="text-lg font-medium mb-4">Comissões Cadastradas</h3>
          {loading && serviceId ? (
            <div className="text-center py-4">Carregando...</div>
          ) : (
            (() => {
              const commissionsToShow = serviceId
                ? userServiceCommissions?.data || []
                : localCommissions;

              return commissionsToShow.length > 0 ? (
                <div className="space-y-2">
                  {commissionsToShow.map((commission: any, index: number) => (
                    <div
                      key={serviceId ? commission.id : index}
                      className="flex justify-between items-center p-3 border rounded"
                    >
                      <div>
                        <span className="font-medium">
                          {commission.user?.name || commission.user_name}
                        </span>
                        <span className="ml-4 text-gray-600">
                          {Math.floor(commission.commission)}%
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <ButtonIcon
                          icon={<Pencil size={14} />}
                          onClick={() => {
                            if (serviceId) {
                              handleEditCommission(commission);
                            } else {
                              const commissionWithIndex = {
                                ...commission,
                                localIndex: index,
                              };
                              handleEditCommission(commissionWithIndex);
                            }
                          }}
                        />
                        <ButtonIcon
                          icon={<Trash size={14} />}
                          hovercolor="red"
                          onClick={() =>
                            handleDeleteCommission(
                              serviceId ? commission.id : index,
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Nenhuma comissão personalizada cadastrada
                </div>
              );
            })()
          )}
        </div>
      </div>

      <AlertModal
        messageAlert={
          dict["Are you sure you want to delete this item?"] ||
          "Tem certeza que deseja excluir este item?"
        }
        open={openAlert}
        onOpenChange={setOpenAlert}
        confirm={handleConfirmDelete}
        cancel={handleCancelDelete}
      />
    </Modal>
  );
};

export default UserServiceCommissionModal;
